// Contact Form component
import React from "react";
import { useTranslation } from "react-i18next";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import type { CartTheme } from "../types";

interface ContactFormProps {
    firstName: string;
    setFirstName: (value: string) => void;
    lastName: string;
    setLastName: (value: string) => void;
    mobileNumber: string;
    setMobileNumber: (value: string) => void;
    orderNote: string;
    setOrderNote: (value: string) => void;
    phoneError: string;
    setPhoneError: (value: string) => void;
    isPhoneFocused: boolean;
    setIsPhoneFocused: (value: boolean) => void;
    detectedCountry: string | undefined;
    selectedCountry: string | undefined;
    setSelectedCountry: (value: string | undefined) => void;
    validatePhoneNumber: (phone: string) => boolean;
    theme?: CartTheme | null;
}

export const ContactForm: React.FC<ContactFormProps> = ({
    firstName,
    setFirstName,
    lastName,
    setLastName,
    mobileNumber,
    setMobileNumber,
    orderNote,
    setOrderNote,
    phoneError,
    setPhoneError,
    isPhoneFocused,
    setIsPhoneFocused,
    detectedCountry,
    selectedCountry,
    setSelectedCountry,
    validatePhoneNumber,
    theme,
}) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === "ar";

    const resolvedPrimaryColor = theme?.primaryColor || "#ea580c";
    const resolvedBorderColor = theme?.borderColor || "#d1d5db";
    const resolvedBackgroundColor = theme?.backgroundColor || "#ffffff";
    const resolvedTextColor = theme?.textColor || "#1f2937";


    const handlePhoneChange = (value: string | undefined) => {
        setMobileNumber(value || "");

        if (!value || value.trim() === "") {
            setPhoneError("");
            setSelectedCountry(detectedCountry || undefined);
        } else if (!validatePhoneNumber(value)) {
            const digitsOnly = value.replace(/\D/g, "");
            if (digitsOnly.length < 8) {
                setPhoneError(
                    t("menu.cart.phoneInvalid", {
                        defaultValue: "Phone number must have at least 8 digits",
                    })
                );
            } else if (digitsOnly.length > 15) {
                setPhoneError(
                    t("menu.cart.phoneTooLong", {
                        defaultValue: "Phone number must not exceed 15 digits",
                    })
                );
            } else {
                setPhoneError(
                    t("menu.cart.phoneFormat", {
                        defaultValue: "Invalid phone number format",
                    })
                );
            }
        } else {
            setPhoneError("");
        }

        if (value) {
            try {
                const { parsePhoneNumberFromString } = require("libphonenumber-js");
                const parsed = parsePhoneNumberFromString(value);
                if (parsed?.country) {
                    setSelectedCountry(parsed.country);
                }
            } catch (e) {
                // Parsing failed, ignore
            }
        }
    };

    const handlePhoneBlur = () => {
        setIsPhoneFocused(false);
        if (mobileNumber?.trim() && !validatePhoneNumber(mobileNumber)) {
            const digitsOnly = mobileNumber.replace(/\D/g, "");
            if (digitsOnly.length < 8) {
                setPhoneError(
                    t("menu.cart.phoneInvalid", {
                        defaultValue: "Phone number must have at least 8 digits",
                    })
                );
            } else if (digitsOnly.length > 15) {
                setPhoneError(
                    t("menu.cart.phoneTooLong", {
                        defaultValue: "Phone number must not exceed 15 digits",
                    })
                );
            } else {
                setPhoneError(
                    t("menu.cart.phoneFormat", {
                        defaultValue: "Invalid phone number format",
                    })
                );
            }
        } else if (!mobileNumber?.trim()) {
            setPhoneError("");
        }
    };

    const inputFocusStyle = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        e.target.style.borderColor = resolvedPrimaryColor;
        e.target.style.boxShadow = `0 0 0 3px ${resolvedPrimaryColor}30`;
        e.target.style.backgroundColor = `${resolvedPrimaryColor}08`;
    };

    const inputBlurStyle = (
        e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
        hasError: boolean = false
    ) => {
        e.target.style.borderColor = hasError ? "#ef4444" : resolvedBorderColor;
        e.target.style.boxShadow = "none";
        e.target.style.backgroundColor = resolvedBackgroundColor;
    };

    return (
        <div className="pt-3 space-y-3">
            {/* First Name */}
            <div className={`flex flex-col md:flex-row ${isRTL ? 'md:space-x-reverse md:space-x-3' : 'md:space-x-3'} space-y-3 md:space-y-0`}>
        
            <div>
                <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: resolvedTextColor }}
                >
                    {t("menu.cart.firstName", { defaultValue: "First Name" })}
                    <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                    type="text"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder={t("menu.cart.firstNamePlaceholder", { defaultValue: "First Name" })}
                    dir={isRTL ? "rtl" : "ltr"}
                    autoComplete="given-name"
                    name="firstName"
                    className="w-full px-4 py-2.5 border-2 rounded-lg focus:ring-2 focus:border-transparent transition-all outline-none"
                    style={{
                        color: resolvedTextColor,
                        backgroundColor: resolvedBackgroundColor,
                        borderColor:
                            firstName.trim().length > 0 && firstName.trim().length < 2
                                ? "#ef4444"
                                : resolvedBorderColor,
                        textAlign: isRTL ? "right" : "left",
                    }}
                    onFocus={inputFocusStyle}
                    onBlur={(e) =>
                        inputBlurStyle(e, firstName.trim().length > 0 && firstName.trim().length < 2)
                    }
                />
                {firstName.trim().length > 0 && firstName.trim().length < 2 && (
                    <p className="text-xs text-red-500 mt-1">
                        {t("menu.cart.firstNameError", {
                            defaultValue: "First name must be at least 2 characters",
                        })}
                    </p>
                )}
            </div>

            {/* Last Name */}
            <div>
                <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: resolvedTextColor }}
                >
                    {t("menu.cart.lastName", { defaultValue: "Last Name" })}
                    <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                    type="text"
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder={t("menu.cart.lastNamePlaceholder", { defaultValue: "Last Name" })}
                    dir={isRTL ? "rtl" : "ltr"}
                    autoComplete="family-name"
                    name="lastName"
                    className="w-full px-4 py-2.5 border-2 rounded-lg focus:ring-2 focus:border-transparent transition-all outline-none"
                    style={{
                        color: resolvedTextColor,
                        backgroundColor: resolvedBackgroundColor,
                        borderColor:
                            lastName.trim().length > 0 && lastName.trim().length < 2
                                ? "#ef4444"
                                : resolvedBorderColor,
                        textAlign: isRTL ? "right" : "left",
                    }}
                    onFocus={inputFocusStyle}
                    onBlur={(e) =>
                        inputBlurStyle(e, lastName.trim().length > 0 && lastName.trim().length < 2)
                    }
                />
                {lastName.trim().length > 0 && lastName.trim().length < 2 && (
                    <p className="text-xs text-red-500 mt-1">
                        {t("menu.cart.lastNameError", {
                            defaultValue: "Last name must be at least 2 characters",
                        })}
                    </p>
                )}
            </div>
</div>
            {/* Order Note */}
            <div>
                <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: resolvedTextColor }}
                >
                    {t("menu.cart.orderNoteLabel", { defaultValue: "Order Note" })}
                </label>
                <textarea
                    value={orderNote}
                    onChange={(e) => setOrderNote(e.target.value)}
                    placeholder={t("menu.cart.orderNotePlaceholder", {
                        defaultValue: "Add any instructions you want the restaurant to know",
                    })}
                    rows={3}
                    className="w-full px-4 py-2.5 border-2 rounded-lg focus:ring-2 focus:border-transparent transition-all outline-none resize-none"
                    style={{
                        color: resolvedTextColor,
                        backgroundColor: resolvedBackgroundColor,
                        borderColor: resolvedBorderColor,
                        textAlign: isRTL ? "right" : "left",
                    }}
                    dir={isRTL ? "rtl" : "ltr"}
                    onFocus={inputFocusStyle}
                    onBlur={(e) => inputBlurStyle(e)}
                    onClick={(e) => e.stopPropagation()}
                />
            </div>

            {/* Phone Number */}
            <div>
                <label
                    className="block text-sm font-medium mb-2"
                    style={{ color: resolvedTextColor }}
                >
                    {t("menu.cart.mobile")}
                    <span className="text-red-500 ml-1">*</span>
                </label>
                <style>
                    {`
                        .phone-input-container .PhoneInputInput {
                            border: none !important;
                            outline: none !important;
                            background: transparent !important;
                            box-shadow: none !important;
                            width: 100%;
                            padding: 0;
                            font-size: 1rem;
                            color: ${resolvedTextColor};
                        }
                        .phone-input-container .PhoneInputInput:focus {
                            outline: none !important;
                            box-shadow: none !important;
                        }
                        .phone-input-container .PhoneInputCountry {
                            margin-right: 0.75rem;
                        }
                        .phone-input-container .PhoneInputCountrySelect {
                            border: none;
                            background: transparent;
                        }
                    `}
                </style>
                <div
                    className={`phone-input-container w-full px-4 py-2.5 border-2 rounded-lg transition-all flex items-center gap-2`}
                    style={{
                        borderColor: phoneError
                            ? "#ef4444"
                            : isPhoneFocused
                                ? resolvedPrimaryColor
                                : resolvedBorderColor,
                        backgroundColor: isPhoneFocused
                            ? phoneError
                                ? "#ef444410"
                                : `${resolvedPrimaryColor}08`
                            : resolvedBackgroundColor,
                        boxShadow: isPhoneFocused
                            ? `0 0 0 3px ${phoneError ? "#ef444460" : `${resolvedPrimaryColor}30`}`
                            : "none",
                    }}
                >
                    <PhoneInput
                        className="w-full"
                        international
                        defaultCountry={(selectedCountry || detectedCountry) as any}
                        value={mobileNumber}
                        onCountryChange={(country: any) => {
                            setSelectedCountry(country || undefined);
                        }}
                        onChange={handlePhoneChange}
                        onFocus={() => setIsPhoneFocused(true)}
                        onBlur={handlePhoneBlur}
                        placeholder={t("menu.cart.mobilePlaceholder")}
                    />
                </div>
                {phoneError && (
                    <p className="text-sm text-red-500 mt-1.5 flex items-center gap-1">
                        <span>⚠</span>
                        {phoneError}
                    </p>
                )}
            </div>
        </div>
    );
};

export default ContactForm;
